import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { SideDrawer, Typography } from '@peritasai/ui-components';
import { SubmitButton } from '../shared/SubmitButton';
import type { AssetCatalogItemEntity, AssetEntity } from '@peritasai/repository';
import type { AssetStatus } from '@peritasai/types';
import {
  useAssetCatalogItem,
  useAssetCatalog,
  useCreateAsset,
  useUpdateAsset,
} from '@peritasai/repository';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerMode } from '../../utils/drawer';
import { formatAssetType } from '../../utils/formatAssetType';

export { DrawerMode as TrackedAssetDrawerMode };

const STATUS_OPTIONS = [
  'AVAILABLE',
  'ALLOCATED',
  'ASSEMBLED',
  'IN_ASSEMBLY',
  'UNAVAILABLE',
  'CHECK_IN',
  'CHECKING_IN',
  'CHECKED_IN',
] as const;

interface TrackedAssetDrawerProps {
  open: boolean;
  mode: DrawerMode;
  selectedAsset: AssetEntity | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormState {
  catalogItem: AssetCatalogItemEntity | null;
  catalogSearch: string;
  referenceNumber: string;
  name: string;
  status: string;
}

interface FormErrors {
  catalogItem?: string;
  referenceNumber?: string;
  apiError?: string;
}

const emptyForm: FormState = {
  catalogItem: null,
  catalogSearch: '',
  referenceNumber: '',
  name: '',
  status: '',
};

function validate(
  form: FormState,
  mode: DrawerMode,
  t: (key: string, options?: Record<string, string>) => string
): FormErrors {
  const errors: FormErrors = {};
  if (mode === DrawerMode.Create) {
    if (!form.catalogItem) {
      errors.catalogItem = t('pages.entityManagement.drawer.fieldRequired', {
        fieldName: t('pages.entityManagement.trackedAssetDrawer.catalogItemLabel'),
      });
    }
    if (!form.referenceNumber.trim()) {
      errors.referenceNumber = t('pages.entityManagement.drawer.fieldRequired', {
        fieldName: t('pages.entityManagement.trackedAssetDrawer.referenceNumberLabel'),
      });
    }
  }
  return errors;
}

export const TrackedAssetDrawer = ({
  open,
  mode,
  selectedAsset,
  onClose,
  onSuccess,
}: TrackedAssetDrawerProps) => {
  const { t } = useTranslation(['periverse']);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState(false);

  const createMutation = useCreateAsset();
  const updateMutation = useUpdateAsset();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const { data: catalogData, isLoading: isCatalogLoading } = useAssetCatalog(
    { name: form.catalogSearch, isActive: true, limit: 20 },
    { enabled: open },
  );
  const catalogOptions = catalogData?.assetCatalogItems ?? [];

  const editCatalogId =
    mode === DrawerMode.Edit && open ? (selectedAsset?.assetCatalogId ?? null) : null;
  const { data: editCatalogItem } = useAssetCatalogItem(editCatalogId ?? '');

  const entityName = t('pages.entityManagement.trackedAssetDrawer.entityName');

  useEffect(() => {
    if (!open) return;
    if (mode === DrawerMode.Edit && selectedAsset) {
      setForm({
        catalogItem: null,
        catalogSearch: '',
        referenceNumber: selectedAsset.referenceNumber,
        name: selectedAsset.name,
        status: selectedAsset.status ?? '',
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched(false);
  }, [open, mode, selectedAsset]);

  useEffect(() => {
    if (mode === DrawerMode.Edit && editCatalogItem) {
      setForm((prev) => ({ ...prev, catalogItem: editCatalogItem, catalogSearch: editCatalogItem.name }));
    }
  }, [mode, editCatalogItem]);

  const handleSubmit = async () => {
    setTouched(true);
    const validationErrors = validate(form, mode, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      if (mode === DrawerMode.Create && form.catalogItem) {
        await createMutation.mutateAsync({
          catalogItemId: form.catalogItem.id,
          referenceNumber: form.referenceNumber.trim(),
          ...(form.name.trim() && { name: form.name.trim() }),
          ...(form.status && { status: form.status as AssetStatus }),
        });
        setForm({
          catalogItem: form.catalogItem,
          catalogSearch: form.catalogSearch,
          referenceNumber: '',
          name: '',
          status: '',
        });
        setTouched(false);
        onSuccess?.();
      } else if (mode === DrawerMode.Edit && selectedAsset) {
        await updateMutation.mutateAsync({
          id: selectedAsset.id,
          dto: {
            ...(form.referenceNumber.trim() && {
              referenceNumber: form.referenceNumber.trim(),
            }),
            ...(form.status && { status: form.status as AssetStatus }),
          },
        });
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('pages.entityManagement.drawer.errorUnexpected');
      setErrors((prev) => ({ ...prev, apiError: message }));
    }
  };

  return (
    <SideDrawer
      open={open}
      title={
        mode === DrawerMode.Create
          ? t('pages.entityManagement.drawer.titleCreate', { entityName })
          : t('pages.entityManagement.drawer.titleEdit', { entityName })
      }
      onClose={onClose}
      footer={
        <SubmitButton disabled={isPending} onClick={() => void handleSubmit()}>
          {isPending
            ? t('pages.entityManagement.drawer.submitting')
            : t('pages.entityManagement.drawer.submit')}
        </SubmitButton>
      }
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          <Autocomplete
            disabled={mode === DrawerMode.Edit}
            options={catalogOptions}
            getOptionKey={(opt) => opt.id}
            getOptionLabel={(opt) => `${formatAssetType(opt.type)} ${opt.trayType !== null ? `(${t(`pages.entityManagement.assetCatalogDrawer.trayTypeOptions.${opt.trayType}`)})` : ''} - ${opt.name}`}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={form.catalogItem}
            inputValue={form.catalogItem ? `${formatAssetType(form.catalogItem.type)} ${form.catalogItem.trayType !== null ? `(${t(`pages.entityManagement.assetCatalogDrawer.trayTypeOptions.${form.catalogItem.trayType}`)})` : ''} - ${form.catalogSearch}` : form.catalogSearch}
            loading={isCatalogLoading}
            filterOptions={(x) => x}
            onInputChange={(_, value) => {
                setForm((prev) => ({ ...prev, catalogItem: null, catalogSearch: value }));
            }}
            onChange={(_, value) => {
              setForm((prev) => ({ ...prev, catalogItem: value, catalogSearch: value?.name ?? '' }));
              if (touched) {
                setErrors((prev) => ({ ...prev, catalogItem: undefined }));
              }
            }}
            renderInput={({ InputProps, inputProps, InputLabelProps, id, disabled, fullWidth }) => (
              <TextField
                id={id}
                disabled={disabled}
                fullWidth={fullWidth}
                label={`${t('pages.entityManagement.trackedAssetDrawer.catalogItemLabel')} *`}
                size="small"
                error={touched && !!errors.catalogItem}
                helperText={touched ? errors.catalogItem : undefined}
                slotProps={{
                  input: InputProps as object,
                  htmlInput: inputProps,
                  inputLabel: InputLabelProps,
                  formHelperText: { sx: { ml: '5px' } },
                }}
              />
            )}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.trackedAssetDrawer.referenceNumberLabel')} *`}
            fullWidth
            size="small"
            value={form.referenceNumber}
            onChange={(e) => {
              const next = { ...form, referenceNumber: e.target.value };
              setForm(next);
              if (touched) setErrors(validate(next, mode, t));
            }}
            disabled={mode === DrawerMode.Edit}
            error={touched && !!errors.referenceNumber}
            helperText={touched ? errors.referenceNumber : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            label={t('pages.entityManagement.trackedAssetDrawer.nameLabel')}
            fullWidth
            size="small"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            disabled={mode === DrawerMode.Edit}
          />
        </Grid>

        <Grid size={6}>
          <FormControl fullWidth size="small">
            <InputLabel>
              {t('pages.entityManagement.trackedAssetDrawer.statusLabel')}
            </InputLabel>
            <Select
              value={form.status}
              label={t('pages.entityManagement.trackedAssetDrawer.statusLabel')}
              onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {touched && errors.apiError && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: 'error.main', mt: 0.5 }}>
            {errors.apiError}
          </Typography>
        </Box>
      )}
    </SideDrawer>
  );
};
